import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Price, PriceKind, SelectedPrice } from '@product/const';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';
import { CustomProductActions } from '../store/actions';
import { StateWithProduct } from '../store/product-state';
import { ProductSelectors } from '../store/selectors';
import { PriceService } from './price.service';

@Injectable({ providedIn: 'root' })
export class SelectedPriceService {
  constructor(
    private store: Store<StateWithProduct>,
    private priceService: PriceService,
  ) {}

  getSelectedPrice(productCode: string): Observable<SelectedPrice> {
    return combineLatest([
      this.store.select(ProductSelectors.getSelectedPriceValue(productCode)),
      this.priceService.getPrices(productCode),
    ]).pipe(
      map(([selectedPrice, prices]) => ({ selectedPrice, prices })),
      filter(({ prices }) => Boolean(prices)),
      map(({ selectedPrice, prices }) => {
        if (!selectedPrice || !this.selectedPriceIsLoaded(prices, selectedPrice)) {
          return this.getFirstRegularPrice(prices);
        }

        return selectedPrice;
      }),
    );
  }

  selectPrice(productCode: string, price: SelectedPrice): void {
    this.store.dispatch(new CustomProductActions.SelectPrice(productCode, price));
  }

  selectPriceByUnitCode(productCode: string, unitCode: string): void {
    this.priceService
      .getPricesByUnit({ unitCode, productCode })
      .pipe(
        filter<Price[]>(Boolean),
        withLatestFrom(this.getSelectedPrice(productCode)),
        map(([prices, selectedPrice]) => {
          return prices.find(({ kind }) => kind === selectedPrice.kind);
        }),
        filter(Boolean),
        take(1),
      )
      .subscribe((newPrice: SelectedPrice) => {
        this.selectPrice(productCode, newPrice);
      });
  }

  getPricesBySelectedUnit(productCode: string): Observable<Price[]> {
    return combineLatest([
      this.getSelectedPrice(productCode),
      this.priceService.getPrices(productCode).pipe(filter<Price[]>(Boolean)),
    ]).pipe(
      map(([selectedPrice, prices]) => {
        return prices.filter((price) => price.unit?.code === selectedPrice.unit?.code);
      }),
    );
  }

  clearSelectedPrice(productCode: string): void {
    this.store.dispatch(new CustomProductActions.SelectedPriceReset(productCode));
  }

  hasPersonalPrice(productCode: string): Observable<boolean> {
    return this.getPricesBySelectedUnit(productCode).pipe(
      map((prices) => {
        const personalPrice = prices.find(({ kind }) => kind === PriceKind.PERSONAL);
        const regularPrice = prices.find(({ kind }) => kind === PriceKind.REGULAR);
        const crossedPrice = prices.find(({ kind }) => kind === PriceKind.CROSSED);

        return Boolean(personalPrice?.value < regularPrice?.value || !crossedPrice ? personalPrice : undefined);
      }),
    );
  }

  private pricesIsEqual(price1: Price, price2: Price): boolean {
    return price1.kind === price2.kind && price1.unit?.code === price2.unit?.code;
  }

  private selectedPriceIsLoaded(prices: Price[], selectedPrice: SelectedPrice): boolean {
    return prices.some((price) => this.pricesIsEqual(price, selectedPrice));
  }

  private getFirstRegularPrice(prices: Price[]): SelectedPrice {
    return prices.find(({ kind }) => kind === PriceKind.REGULAR) as SelectedPrice;
  }
}
